<template>
<div>
  <div>
    <table class="rounds-table table table-bordered table-hover text-nowrap">
      <thead class="thead-dark">
        <tr>
          <th>Round</th>
          <th>Stage</th>
          <th>Participant count</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="round in rounds.rounds" :key="round.round_id">
            <td>{{ round.round_id }}</td>
            <td>{{ round.stage_id }}</td>
            <td>
              <b-button v-b-modal.participants-modal size="sm" @click="loadParticipants(regattaId, round)" variant="link">
                {{ round.participant_count }}
              </b-button>
            </td>
        </tr>
      </tbody>
    </table>

    <b-pagination-nav
        v-if="pageAmount > 1"
        :number-of-pages="pageAmount"
        :link-gen="linkGen"
        first-number
        last-number
        use-router
        class="mb-4"
    />

    <b-modal id="participants-modal" title="Participants" ok-only ok-title="Close" size="xl">
      <table class="table">
        <thead class="thead-dark">
          <tr>
            <th>Player</th>
            <th>State</th>
            <th>Joined at</th>
            <th>Finished at</th>
            <th>Scores</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(participant, index) in participants" :key="index">
            <td>
              <b-link :to="{name: 'player_info', params: { playerId: participant.player_id }}">
                {{ participant.player_id }}
              </b-link>
            </td>
            <td :class="participantStatusClass(participant.status)">{{ participant.status | participantStatusStr }}</td>
            <td>{{ participant.join_at | getMomentStr }}</td>
            <td>{{ participant.end_at | getMomentStr }}</td>
            <b-button v-b-modal.participant-scores-modal size="sm" @click="loadScores(regattaId, participant)" variant="link">
              {{ participant.scores }}
            </b-button>
<!--            <td>{{ participant.scores }}</td>-->
          </tr>
        </tbody>
      </table>
    </b-modal>

    <loading-indicator v-if="isLoading"></loading-indicator>

    <b-modal id="participant-scores-modal" title="Participant scores" ok-only ok-title="Close" size="xl">
      <table class="table">
        <thead class="thead-dark">
        <tr>
          <th>Score</th>
          <th>Time after joining a round</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(score, index) in scores" :key="index">
          <td>{{ score.score }}</td>
          <td>{{ score.created_at | convertDurationSecToShortStr }}</td>
        </tr>
        </tbody>
      </table>
    </b-modal>

  </div>
</div>
</template>

<script>
import LoadingIndicator from '../LoadingIndicator.vue';

import {HTTP} from '../../http-common.js';
import notifier from '../../notifier.js';
import { Participant } from '../../model/regatta.js';
import {convertDurationSecToShortStr} from "@/util";

export default {
  name: 'RegattaRoundList',
  components: {
    LoadingIndicator,
  },

  props: {
    regattaId: {
      type: Number,
      required: true,
    },
    page: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      isLoading: false,
      rounds: {},
      participants: [],
      scores:[],
      roundsAmount: null,
    };
  },

  computed: {
    /** @returns {number} */
    pageAmount() {
      const roundsAmount = this.roundsAmount;
      if(roundsAmount === null) {
        return 0;
      }

      return Math.ceil(roundsAmount / 100);
    },
  },

  created() {
    this.loadRounds(this.regattaId);
  },

  watch: {
    page() {
      this.loadRounds(this.regattaId);
    },
  },

  methods: {
    loadRounds(regattaId) {
      notifier.deleteSenderNotifications(this);

      this.isLoading = true;

      const urlParams = new URLSearchParams(window.location.search);

      const limit = Number(urlParams.get('limit') || 100);
      const page = Number(urlParams.get('page') || 1);
      const offset = page < 1 ? 0 : (page - 1) * limit;

      //console.log("page: " ,page)
      //console.log("offset: " ,offset)

      HTTP.get(`/regattas/${regattaId}/rounds?limit=${limit}&offset=${offset}`)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.rounds = data;
        this.roundsAmount = data.roundsAmount
      })
      .catch(e => {
        notifier.showError(e, {sender: this});
      })
      .finally(() => {
        this.isLoading = false;
      });
    },

    loadParticipants(regattaId, round) {
      notifier.deleteSenderNotifications(this);

      if (round.participant_count == 0) {
        this.participants = [];
        return;
      }

      HTTP.get(`/regattas/${regattaId}/rounds/${round.round_id}/participants`)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        this.participants = data.participants || [];
      })
      .catch(e => {
        notifier.showError(e, {sender: this});
      });
    },

    loadScores(regattaId, participant) {
      notifier.deleteSenderNotifications(this);

      if (participant.scores == 0) {
        this.scores = [];
        return;
      }

      HTTP.get(`/regattas/${regattaId}/rounds/${participant.round_id}/players/${participant.player_id}/scores`)
          .then(response => {
            const data = response.data.data;
            if(data === null) {
              notifier.showError('Invalid server response. data === null.', {sender: this});
              return;
            }

            this.scores = data.scores || [];
          })
          .catch(e => {
            notifier.showError(e, {sender: this});
          });
    },

    participantStatusClass(status) {
      return {
        'status-prtcpnt-in-progress': Participant.inProgress(status),
        'status-prtcpnt-finished': Participant.isFinished(status),
      };
    },

    linkGen(pageNum) {
      return pageNum === 1 ? '?' : `?page=${pageNum}`;
    },
  },

  filters: {
    participantStatusStr: status => Participant.getStatusName(status),
    convertDurationSecToShortStr,
  },
}
</script>

<style scoped>
.rounds-table {
  width: 1px;
}

.status-closed {
  background-color: #fcf8e3;
}

.status-open {
  background-color: #dff0d8;
}

.status-finished {
  background-color: #f2dede;
}

.status-prtcpnt-in-progress {
  background-color: #fcf8e3;
}

.status-prtcpnt-finished {
  background-color: #dff0d8;
}
</style>
